/**
 * Scope 3 Management Component - Modern Visual Design
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  Button,
  LinearProgress,
  Alert,
  Tooltip,
  IconButton,
  Tab,
  Tabs,
  CircularProgress,
} from '@mui/material';
import {
  Upload,
  Download,
  TrendingUp,
  ArrowUpward,
  ArrowDownward,
  LocalShipping,
  Factory,
  FlightTakeoff,
  DirectionsCar,
  Delete as DeleteIcon,
  Recycling,
  Business,
  ShoppingCart,
  ElectricBolt,
  Refresh,
  MoreVert,
} from '@mui/icons-material';
import { useApp } from '../../contexts/AuthContext';
import {
  scope3SubmissionService,
  Scope3SubmissionSummary,
  Scope3CategorySummary,
  SCOPE3_CATEGORIES,
} from '../../services/scope3SubmissionService';
import { Scope3SubmissionUpload } from './Scope3SubmissionUpload';
import { Scope3SubmissionsList } from './Scope3SubmissionsList';
import { EmissionsTrendChart } from '../shared/EmissionsTrendChart';

// Category icons mapping
const CATEGORY_ICONS: Record<number, React.ReactElement> = {
  1: <ShoppingCart sx={{ fontSize: 18 }} />,
  2: <Factory sx={{ fontSize: 18 }} />,
  3: <ElectricBolt sx={{ fontSize: 18 }} />,
  4: <LocalShipping sx={{ fontSize: 18 }} />,
  5: <DeleteIcon sx={{ fontSize: 18 }} />,
  6: <FlightTakeoff sx={{ fontSize: 18 }} />,
  7: <DirectionsCar sx={{ fontSize: 18 }} />,
  8: <Business sx={{ fontSize: 18 }} />,
  9: <LocalShipping sx={{ fontSize: 18 }} />,
  10: <Factory sx={{ fontSize: 18 }} />,
  11: <TrendingUp sx={{ fontSize: 18 }} />,
  12: <Recycling sx={{ fontSize: 18 }} />,
  13: <Business sx={{ fontSize: 18 }} />,
  14: <Business sx={{ fontSize: 18 }} />,
  15: <TrendingUp sx={{ fontSize: 18 }} />,
};

// Short category names for compact display
const CATEGORY_SHORT_NAMES: Record<number, string> = {
  1: 'Purchased Goods',
  2: 'Capital Goods',
  3: 'Fuel & Energy',
  4: 'Upstream Transport',
  5: 'Waste',
  6: 'Business Travel',
  7: 'Commuting',
  8: 'Leased Assets',
  9: 'Downstream Transport',
  10: 'Processing',
  11: 'Product Use',
  12: 'End of Life',
  13: 'Leased Assets',
  14: 'Franchises',
  15: 'Investments',
};

const UPSTREAM_CATEGORIES = [1, 2, 3, 4, 5, 6, 7, 8];
const DOWNSTREAM_CATEGORIES = [9, 10, 11, 12, 13, 14, 15];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const Scope3Management: React.FC = () => {
  const { selectedPeriod } = useApp();

  const [summary, setSummary] = useState<Scope3SubmissionSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [preselectedCategory, setPreselectedCategory] = useState<number | undefined>();
  const [downloadingTemplate, setDownloadingTemplate] = useState<number | null>(null);

  const fetchSummary = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const reportingPeriod = selectedPeriod ? `FY${selectedPeriod.year}` : undefined;
      const data = await scope3SubmissionService.getSummary(reportingPeriod);
      setSummary(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load Scope 3 data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleDownloadTemplate = async (category: number) => {
    setDownloadingTemplate(category);
    try {
      const { url, filename } = await scope3SubmissionService.downloadTemplate(category);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download template');
    } finally {
      setDownloadingTemplate(null);
    }
  };

  const handleUploadClick = (category: number) => {
    setPreselectedCategory(category);
    setUploadDialogOpen(true);
  };

  const getCategorySummary = (categoryNumber: number): Scope3CategorySummary | undefined => {
    return summary?.by_category.find((c) => c.category_number === categoryNumber);
  };

  const totalScope3 = summary?.total_tco2e || 0;
  const upstreamTotal = summary?.by_category
    .filter(c => UPSTREAM_CATEGORIES.includes(c.category_number))
    .reduce((sum, c) => sum + c.total_tco2e, 0) || 0;
  const downstreamTotal = summary?.by_category
    .filter(c => DOWNSTREAM_CATEGORIES.includes(c.category_number))
    .reduce((sum, c) => sum + c.total_tco2e, 0) || 0;
  const completedCategories = summary?.by_category.filter(c => c.total_tco2e > 0).length || 0;

  // Compact Category Row
  const CategoryRow: React.FC<{ categoryNumber: number }> = ({ categoryNumber }) => {
    const catSummary = getCategorySummary(categoryNumber);
    const hasData = catSummary && catSummary.total_tco2e > 0;
    const emissions = catSummary?.total_tco2e || 0;
    const percentage = totalScope3 > 0 ? (emissions / totalScope3) * 100 : 0;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          px: 2,
          borderRadius: 2,
          transition: 'background-color 0.15s',
          '&:hover': {
            backgroundColor: '#f8fafc',
          },
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            backgroundColor: hasData ? '#dbeafe' : '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: hasData ? '#2563eb' : '#9ca3af',
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          {CATEGORY_ICONS[categoryNumber]}
        </Box>

        {/* Name */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#1f2937',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {categoryNumber}. {CATEGORY_SHORT_NAMES[categoryNumber]}
          </Typography>
        </Box>

        {/* Progress bar */}
        <Box sx={{ width: 80, flexShrink: 0 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(percentage, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: '#e5e7eb',
              '& .MuiLinearProgress-bar': {
                backgroundColor: hasData ? '#3b82f6' : '#e5e7eb',
                borderRadius: 3,
              },
            }}
          />
        </Box>

        {/* Value */}
        <Typography
          sx={{
            width: 90,
            textAlign: 'right',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: hasData ? '#1f2937' : '#9ca3af',
            flexShrink: 0,
          }}
        >
          {hasData ? emissions.toLocaleString() : '—'}
        </Typography>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title="Download template">
            <IconButton
              size="small"
              onClick={() => handleDownloadTemplate(categoryNumber)}
              disabled={downloadingTemplate === categoryNumber}
              sx={{
                color: '#9ca3af',
                '&:hover': { color: '#6b7280', backgroundColor: '#f3f4f6' },
              }}
            >
              {downloadingTemplate === categoryNumber ? (
                <CircularProgress size={16} />
              ) : (
                <Download sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Upload data">
            <IconButton
              size="small"
              onClick={() => handleUploadClick(categoryNumber)}
              sx={{
                color: '#3b82f6',
                '&:hover': { backgroundColor: '#dbeafe' },
              }}
            >
              <Upload sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  // Summary Metric Card
  const MetricCard: React.FC<{
    label: string;
    value: string | number;
    subtext?: string;
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
  }> = ({ label, value, subtext, icon, iconBg, iconColor }) => (
    <Card
      sx={{
        p: 2.5,
        border: '1px solid #e5e7eb',
        boxShadow: 'none',
        borderRadius: 3,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            backgroundColor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: iconColor,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.5,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#111827',
              lineHeight: 1.2,
            }}
          >
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          {subtext && (
            <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>
              {subtext}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
          Scope 3 Emissions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={fetchSummary} disabled={isLoading} sx={{ color: '#6b7280' }}>
            <Refresh />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Upload sx={{ fontSize: 18 }} />}
            onClick={() => {
              setPreselectedCategory(undefined);
              setUploadDialogOpen(true);
            }}
            sx={{
              backgroundColor: '#2563eb',
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 'none',
              borderRadius: 2,
              px: 2.5,
              '&:hover': { backgroundColor: '#1d4ed8', boxShadow: 'none' },
            }}
          >
            Upload Data
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Metric Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="Total Scope 3"
                value={totalScope3}
                subtext="tCO2e"
                icon={<TrendingUp />}
                iconBg="#fef3c7"
                iconColor="#d97706"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="Upstream"
                value={upstreamTotal}
                subtext={totalScope3 > 0 ? `${((upstreamTotal / totalScope3) * 100).toFixed(0)}% of total` : 'tCO2e'}
                icon={<ArrowUpward />}
                iconBg="#dbeafe"
                iconColor="#2563eb"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="Downstream"
                value={downstreamTotal}
                subtext={totalScope3 > 0 ? `${((downstreamTotal / totalScope3) * 100).toFixed(0)}% of total` : 'tCO2e'}
                icon={<ArrowDownward />}
                iconBg="#d1fae5"
                iconColor="#059669"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard
                label="Categories"
                value={`${completedCategories}/15`}
                subtext="reported"
                icon={<MoreVert />}
                iconBg="#f3e8ff"
                iconColor="#9333ea"
              />
            </Grid>
          </Grid>

          {/* Emissions Trend Chart */}
          <EmissionsTrendChart
            data={summary?.by_category.map(cat => ({
              category: UPSTREAM_CATEGORIES.includes(cat.category_number) ? 'Upstream' : 'Downstream',
              subcategory: `${cat.category_number}. ${CATEGORY_SHORT_NAMES[cat.category_number] || 'Category ' + cat.category_number}`,
              source: UPSTREAM_CATEGORIES.includes(cat.category_number) ? 'Upstream' : 'Downstream',
              value: cat.total_tco2e,
            })) || []}
            title="Scope 3 Emissions"
            color="#9333ea"
            showScopeTabs={false}
          />

          {/* Main Content */}
          <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ borderBottom: '1px solid #e5e7eb' }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                  px: 2,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    minHeight: 48,
                    px: 2,
                  },
                  '& .Mui-selected': { color: '#111827', fontWeight: 600 },
                  '& .MuiTabs-indicator': { backgroundColor: '#2563eb', height: 2 },
                }}
              >
                <Tab label="Categories" />
                <Tab label="Submissions" />
              </Tabs>
            </Box>

            {/* Categories Tab */}
            <TabPanel value={activeTab} index={0}>
              <Grid container>
                {/* Upstream */}
                <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid #e5e7eb' } }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid #f3f4f6' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          backgroundColor: '#dbeafe',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ArrowUpward sx={{ fontSize: 16, color: '#2563eb' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.9rem' }}>
                        Upstream
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', ml: 'auto' }}>
                        {upstreamTotal.toLocaleString()} tCO2e
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ py: 1 }}>
                    {UPSTREAM_CATEGORIES.map((num) => (
                      <CategoryRow key={num} categoryNumber={num} />
                    ))}
                  </Box>
                </Grid>

                {/* Downstream */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, borderBottom: '1px solid #f3f4f6' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          backgroundColor: '#d1fae5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ArrowDownward sx={{ fontSize: 16, color: '#059669' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.9rem' }}>
                        Downstream
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', ml: 'auto' }}>
                        {downstreamTotal.toLocaleString()} tCO2e
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ py: 1 }}>
                    {DOWNSTREAM_CATEGORIES.map((num) => (
                      <CategoryRow key={num} categoryNumber={num} />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Submissions Tab */}
            <TabPanel value={activeTab} index={1}>
              <Box sx={{ p: 3 }}>
                <Scope3SubmissionsList onRefresh={fetchSummary} />
              </Box>
            </TabPanel>
          </Card>
        </>
      )}

      {/* Upload Dialog */}
      <Scope3SubmissionUpload
        open={uploadDialogOpen}
        onClose={() => {
          setUploadDialogOpen(false);
          setPreselectedCategory(undefined);
        }}
        onSuccess={fetchSummary}
        preselectedCategory={preselectedCategory}
      />
    </Box>
  );
};
